import { useEffect, useRef, useState } from 'react';

const ACTIVITY = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * True once nothing has touched the screen for `ms`.
 *
 * The timer is reset from a ref rather than by re-running the effect, so a passing hand
 * doesn't tear down and rebuild five listeners sixty times a second.
 */
export function useIdle(ms: number, enabled = true): boolean {
  const [idle, setIdle] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      setIdle(false);
      return;
    }

    const reset = (): void => {
      setIdle((was) => (was ? false : was));
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setIdle(true), ms);
    };

    reset();
    for (const e of ACTIVITY) window.addEventListener(e, reset, { passive: true });

    // A tablet that slept and woke should re-arm rather than wake up already-idle.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') reset();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearTimeout(timer.current);
      for (const e of ACTIVITY) window.removeEventListener(e, reset);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ms, enabled]);

  return idle;
}
